import { useNewTranslation } from 'hooks'
import React, { useEffect, useState } from 'react'
import { TemplateFormContainer } from './style'
import { TaskElementProps } from './Actions'
import { CampaignTaskElements } from 'services/api/campaignTemplates'
import { useFormikContext } from 'formik'
import { TemplateDetailSchema } from '../TemplateDetail'
import OnFormikValueChange from '../OnFormikValueChange'
import { useDispatch, useSelector } from 'react-redux'
import { updateCampaignTaskElements, CampaignTemplateStep } from 'state/campaignTemplates'
import { selectTemplateFirstStepDataByTaskIndex } from 'state/appPreview/selector'
import { interpolate, interpolateAndJoin, removeAndFormatTagsValue } from 'components/utils/interpolation'
import { updateigStoryRepostStep1Data } from 'state/appPreview/action/igStoryRepost'
import TemplateTask from './TemplateTask'
import { fieldKeyByTasks } from '../TemplateDetail/options'
import { selectCampaignByTemplateData } from 'state'
import { CAMPAIGN_STATUS_ENUM } from 'state/campaigns'

const fieldNames = fieldKeyByTasks.ig_story_repost

const IGStoryRepost = ({
    showError: baseShowError,
    taskOrder,
    isReadOnly,
    updateCurrentStepStatus,
    setNavigationDisable,
    ...props
}: TaskElementProps) => {
    const [t] = useNewTranslation()
    const { values } = useFormikContext<TemplateDetailSchema>()
    const [showError, setShowError] = useState(baseShowError)
    const dispatch = useDispatch()
    const campaign = useSelector(selectCampaignByTemplateData)

    const selectedTemplateFirstStepData = useSelector(selectTemplateFirstStepDataByTaskIndex(taskOrder))

    const namePrefix = `task_elements.${taskOrder}.`
    const validateFieldsKey = fieldNames.map(field => namePrefix + field) as (keyof TemplateDetailSchema)[]
    const isCampaignLive = campaign?.status === CAMPAIGN_STATUS_ENUM.LIVE

    const wrongFormatCopy = t('campaign.template.profileTag.errorMessage.wrongFormate')

    useEffect(() => {
        handleDebouncedChange(values)
    }, [])

    useEffect(() => {
        setShowError(baseShowError)
    }, [baseShowError])

    const disableAutoSave = () => {
        // Enable data capture and preview update if campaign is live
        if (isCampaignLive) {
            return false
        }

        return isReadOnly
    }

    const onSaveData = (fieldsValues: Partial<TemplateDetailSchema>, showSuccessToast: boolean) => {
        const validTemplateDataUuid = typeof props.templateUuid === 'string' ? props.templateUuid : ''
        const taskValue = (fieldsValues?.task_elements?.[taskOrder] ?? {}) as CampaignTaskElements
        dispatch(
            updateCampaignTaskElements({
                templateUuid: validTemplateDataUuid,
                ...taskValue,
                setNavigationDisable: setNavigationDisable,
                showSuccessToast,
            }),
        )
    }

    const handleDebouncedChange = (fieldsValues: Partial<TemplateDetailSchema>) => {
        const taskValue = (fieldsValues?.task_elements?.[taskOrder] ?? {}) as CampaignTaskElements
        // Since OnFormikValueChange will only assign validated values to handleDebouncedChange,
        // and brand_name is not included in the validation fields, we need to assess it from useFormikContext.
        const brandName = values?.task_elements?.[taskOrder].brand_name ?? ''
        if (taskValue.element) {
            const {
                media_requirement_dos = [],
                media_requirement_donts = [],
                submission_requirement_description,
                caption_requirement_hashtags = [],
                media_requirement_tags = [],
            } = selectedTemplateFirstStepData.step ?? {}
            const defaultHashtags = removeAndFormatTagsValue(caption_requirement_hashtags, '{{caption_hashtags}}')
            const defaultProfileTags = removeAndFormatTagsValue(media_requirement_tags, '{{brand_handles}}')

            const submissionRequirementDescription =
                submission_requirement_description &&
                interpolate(submission_requirement_description, {
                    brand_name: brandName,
                })

            const payload = {
                mediaDos: interpolateAndJoin(media_requirement_dos),
                mediaDonts: interpolateAndJoin(media_requirement_donts),
                captionHashtags: [...defaultHashtags, ...(taskValue?.caption_hashtags ?? [])],
                exampleMediaDescription: taskValue.example_media_description,
                exampleMedia: taskValue?.example_medias,
                submissionRequirementDescription: submissionRequirementDescription,
                profileTags: [...defaultProfileTags, ...(taskValue?.brand_handles ?? [])],
            }
            dispatch(updateigStoryRepostStep1Data(payload))

            if (!isCampaignLive && !isReadOnly) {
                onSaveData(fieldsValues, false)
            }
        }
    }

    const onShowError = () => {
        setShowError(true)
    }

    return (
        <TemplateFormContainer>
            <OnFormikValueChange
                callback={handleDebouncedChange}
                fields={validateFieldsKey}
                skipCallbackError={{
                    [namePrefix + 'brand_handles']: [wrongFormatCopy],
                    [namePrefix + 'caption_hashtags']: [wrongFormatCopy],
                }}
                validateSpecificField={props.validateSpecificField}
                showError={showError}
                updateCurrentStepStatus={updateCurrentStepStatus}
                // Only disableNavigation for input if campaign is not live
                {...(!isCampaignLive && { setDisableNavigation: setNavigationDisable })}
                disableAutoSave={disableAutoSave()}
            />
            <TemplateTask
                elementKey="ig_story_repost"
                field_keys={fieldNames}
                taskOrder={taskOrder}
                updateCurrentStepStatus={updateCurrentStepStatus}
                showError={showError}
                identifierPrefix={CampaignTemplateStep.IG_STORY_REPOST}
                onSaveData={onSaveData}
                onShowError={onShowError}
                fields={validateFieldsKey}
                {...props}
            />
        </TemplateFormContainer>
    )
}

export default IGStoryRepost
